/**
 * System Pulse page routes. The Overview's Pulse routes (pulse-routes.ts) are reused
 * where they fit; these are the rest of what the page reads and writes.
 */
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import type { AlertRule, PulseAnomalies, PulseEnvelope, PulseIncident, PulseIncidents } from './pulse-types';
import type {
  ActivationFlag,
  AlertRuleStats,
  AlertStormReport,
  CanaryReport,
  CanaryRunResult,
  CertReport,
  ConnectorHealthReport,
  DevicesReport,
  HealthReport,
  IncidentDetail,
  IncidentsV3Response,
  InfraProbeReport,
  ModelsResponse,
  OnCallConfigResponse,
  OnCallCurrent,
  OnCallPage,
  OncallV3,
  PathsReport,
  PulseIncidentNote,
  PulseSuite,
  PulseTestJob,
  PulseTestStarted,
  QaRunsReport,
  PhiAccessReport,
  PulseForecasts,
  PulseHandover,
  PulseIncidentExtra,
  PulseProblem,
  PulseProblems,
  PulseProjections,
  PulseSuppression,
  RuleAuditFinding,
  SecurityEventReport,
  SignalBaseline,
  SlaResult,
  SloRow,
} from './system-pulse-types';

const A = AITHORA_PREFIX;

// ── /api/aithora/pulse-v3 (served) ──────────────────────────────────────────

/** GET /pulse-v3/paths?facility=: the latest run of each safety path in one building. */
export const pulsePaths = route<PathsReport, { facility?: string }>()('GET', `${A}/pulse-v3/paths`, { honest: true });
/** GET /pulse-v3/devices?facility=: device check-ins and residents without fall coverage. */
export const pulseDevices = route<DevicesReport, { facility?: string }>()('GET', `${A}/pulse-v3/devices`, { honest: true });
/** GET /pulse-v3/incidents?windowDays=: incidents with changes, runbook, review and facility note. */
export const pulseIncidentsV3 = route<IncidentsV3Response, { windowDays?: number }>()('GET', `${A}/pulse-v3/incidents`, { honest: true });
/** GET /pulse-v3/models: the model registry for the Models tab. */
export const pulseModels = route<ModelsResponse>()('GET', `${A}/pulse-v3/models`, { honest: true });
/** GET /pulse-v3/oncall: the chain, rotation, channels and the weekly test. */
export const pulseOncallV3 = route<OncallV3>()('GET', `${A}/pulse-v3/oncall`, { honest: true });
/** POST /pulse-v3/facilities/:id/chain-test: a real test page to on-shift staff (idempotency key in the body). */
export const pulseChainTest = route<{ real: true; live: boolean; reason: string | null; page_id: string }, void, { idempotency_key: string; confirm: true }>()(
  'POST', `${A}/pulse-v3/facilities/:id/chain-test`, { honest: true });
/** POST /pulse-v3/facilities/:id/fallback: the downtime message to the building contact. */
export const pulseFallback = route<{ real: true; live: boolean; page_id: string }, void, { idempotency_key: string; message?: string; confirm: true }>()(
  'POST', `${A}/pulse-v3/facilities/:id/fallback`, { honest: true });
/** POST /pulse-v3/oncall/test: the weekly test page to whoever is on call. */
export const pulseOncallTest = route<{ real: true; live: boolean; page_id: string }, void, { idempotency_key: string; confirm: true }>()(
  'POST', `${A}/pulse-v3/oncall/test`, { honest: true });
/** GET /pulse-v3/pages/:id: one page with its attempts, delivery and time to ack. */
export const pulsePage = route<{ real: true; page: OnCallPage }>()('GET', `${A}/pulse-v3/pages/:id`, { honest: true });
/** POST /pulse-v3/pages/:id/ack: Aithora writers, or someone the page was sent to. */
export const pulsePageAck = route<{ real: true; page: OnCallPage }, void, Record<string, never>>()(
  'POST', `${A}/pulse-v3/pages/:id/ack`, { honest: true });
/** POST /pulse-v3/pages/:id/escalate {reason?}: send it to the next on-call step now.
 *  Only someone on the page's chain (it was sent to them, or they are on the chain). */
export const pulsePageEscalate = route<{ real: true; escalated: { step: number; round: number }; page: OnCallPage }, void, { reason?: string }>()(
  'POST', `${A}/pulse-v3/pages/:id/escalate`, { honest: true });

// ── /api/pulse (served, envelope) ───────────────────────────────────────────

/**
 * GET /api/pulse/health: every Aithora service, worst-of. `at` (ISO, served): the services
 * as the heartbeat series recorded them at that moment (the last beat within 5 minutes
 * before it, 7 days back at most), so the station's Rewind replays the services too.
 */
export const pulseHealth = route<PulseEnvelope<HealthReport>, { at?: string }>()('GET', '/api/pulse/health');
/** GET /api/pulse/incidents with the ref / openedBy / cause fields (served). */
export const pulseIncidentList = route<PulseEnvelope<Omit<PulseIncidents, 'incidents'> & { incidents: (PulseIncident & PulseIncidentExtra)[] }>, { status?: string; windowDays?: number }>()(
  'GET', '/api/pulse/incidents');
/** GET /api/pulse/incidents/:id: timeline, notes, remediation, escalation. */
export const pulseIncidentDetail = route<PulseEnvelope<IncidentDetail>>()('GET', '/api/pulse/incidents/:id');
/** GET /api/pulse/alert-rules with the rule statistics (served). */
export const pulseRules = route<PulseEnvelope<(AlertRule & AlertRuleStats)[]>>()('GET', '/api/pulse/alert-rules');
/** POST /api/pulse/alert-rules/:id/enabled: mute (enabled false) or unmute a rule. */
export const pulseRuleEnabled = route<PulseEnvelope<AlertRule>, void, { enabled: boolean }>()('POST', '/api/pulse/alert-rules/:id/enabled');
/** GET /api/pulse/alert-storm?windowMinutes=&minIncidents= */
export const pulseAlertStorm = route<PulseEnvelope<AlertStormReport>, { windowMinutes?: number; minIncidents?: number }>()('GET', '/api/pulse/alert-storm');
/** GET /api/pulse/oncall: the tier editor's config. */
export const pulseOncallConfig = route<PulseEnvelope<OnCallConfigResponse>>()('GET', '/api/pulse/oncall');
/** GET /api/pulse/oncall/current: who holds the pager now. */
export const pulseOncallCurrent = route<PulseEnvelope<OnCallCurrent>>()('GET', '/api/pulse/oncall/current');
/** POST /api/pulse/oncall: save tiers and ladders. */
export const pulseOncallSave = route<PulseEnvelope<OnCallConfigResponse>, void, { tiers: { level: number; userIds: string[]; label?: string }[]; ladder: { thresholdsMin: number[] }; criticalLadder: { thresholdsMin: number[] } }>()(
  'POST', '/api/pulse/oncall');
/** GET /api/pulse/connectors */
export const pulseConnectors = route<PulseEnvelope<ConnectorHealthReport>>()('GET', '/api/pulse/connectors');
/** GET /api/pulse/slo (served, aithora/pulse/router.ts). Only SloRow's optional name / objective / measures fields are still PROPOSED. */
export const pulseSloRows = route<PulseEnvelope<{ status: string; slos: SloRow[] }>>()('GET', '/api/pulse/slo');
/** GET /api/pulse/canary */
export const pulseCanary = route<PulseEnvelope<CanaryReport>>()('GET', '/api/pulse/canary');
/** GET /api/pulse/qa-runs: the latest QA crawl from the console and the phone, per role (Role checks). */
export const pulseQaRuns = route<PulseEnvelope<QaRunsReport>>()('GET', '/api/pulse/qa-runs');
/** POST /api/pulse/canary/run?suite=care: run one suite now (read-only). */
export const pulseCanaryRun = route<PulseEnvelope<CanaryRunResult>, { suite?: 'care' }>()('POST', '/api/pulse/canary/run');
/** GET /api/pulse/anomalies (served, aithora/pulse/router.ts). baselineNames is served; only the optional signals are still PROPOSED. */
export const pulseAnomalyReport = route<PulseEnvelope<PulseAnomalies & { signals?: SignalBaseline[]; baselineNames?: Record<string, string> }>>()('GET', '/api/pulse/anomalies');
/** GET /api/pulse/probes */
export const pulseProbes = route<PulseEnvelope<InfraProbeReport>>()('GET', '/api/pulse/probes');
/** One signal's uptime from its recorded beats; null with buildingHistory until there are enough. */
export interface SignalUptime {
  uptimePct: number | null;
  buildingHistory: boolean;
  detail?: string;
}
/** GET /api/pulse/uptime?scope=&scopeId=&signal=&windowDays= (restored from the Flutter Pulse heartbeat line). */
export const pulseUptime = route<PulseEnvelope<SignalUptime>, { scope: string; scopeId?: string; signal: string; windowDays?: number }>()('GET', '/api/pulse/uptime');
/** GET /api/pulse/sla?scope=&windowDays= */
export const pulseSla = route<PulseEnvelope<SlaResult>, { scope: string; windowDays?: number }>()('GET', '/api/pulse/sla');
/** GET /api/pulse/security/certs */
export const pulseCerts = route<PulseEnvelope<CertReport>>()('GET', '/api/pulse/security/certs');
/** GET /api/pulse/security/events */
export const pulseSecurityEvents = route<PulseEnvelope<SecurityEventReport>>()('GET', '/api/pulse/security/events');
/** GET /api/pulse/security/access-anomalies */
export const pulseAccessAnomalies = route<PulseEnvelope<PhiAccessReport>>()('GET', '/api/pulse/security/access-anomalies');

/** POST /api/pulse/incidents/:id/resolve {note?}: a person closes the incident by hand (409 when already resolved). */
export const pulseIncidentResolve = route<PulseEnvelope<PulseIncident>, void, { note?: string }>()('POST', '/api/pulse/incidents/:id/resolve');
/** POST /api/pulse/incidents/:id/notes {note, kind}: append a postmortem note (the trail is append-only). */
export const pulseIncidentNote = route<PulseEnvelope<PulseIncidentNote>, void, { note: string; kind?: 'note' | 'cause' | 'action-item' }>()(
  'POST', '/api/pulse/incidents/:id/notes');
/** POST /api/pulse/test/run {suite}: start the read-only smoke or deep probe run. */
export const pulseTestRun = route<PulseTestStarted, void, { suite: PulseSuite }>()('POST', '/api/pulse/test/run');
/** GET /api/pulse/test/:jobId: poll a run until it completes. */
export const pulseTestJob = route<PulseEnvelope<PulseTestJob>>()('GET', '/api/pulse/test/:jobId');

// ── The station (served: v3/pulse/{problems,forecasts,handover,reveal}.ts, pulse/station-api.ts) ──

/** GET /pulse-v3/problems?at=: the Act now queue (at = rewind to that moment). */
export const pulseProblems = route<PulseProblems, { at?: string }>()('GET', `${A}/pulse-v3/problems`, { honest: true });
/** POST /pulse-v3/problems/:id/take: take ownership. */
export const pulseProblemTake = route<{ real: true; problem: PulseProblem }>()('POST', `${A}/pulse-v3/problems/:id/take`, { honest: true });
/** POST /pulse-v3/problems/:id/ack: acknowledge a problem that has no incident. */
export const pulseProblemAck = route<{ real: true; problem: PulseProblem }>()('POST', `${A}/pulse-v3/problems/:id/ack`, { honest: true });
/** GET /pulse-v3/forecasts: the Coming up list. */
export const pulseForecasts = route<PulseForecasts>()('GET', `${A}/pulse-v3/forecasts`, { honest: true });
/** GET /pulse-v3/handover: the last signed handover. */
export const pulseHandover = route<PulseHandover>()('GET', `${A}/pulse-v3/handover`, { honest: true });
/** POST /pulse-v3/handover/sign: sign and accept the handover (logged). */
export const pulseHandoverSign = route<PulseHandover, void, { confirm: true }>()('POST', `${A}/pulse-v3/handover/sign`, { honest: true });
/** POST /pulse-v3/reveal: show one masked resident name, with a reason (sealed to the Truth Ledger). */
export const pulseReveal = route<{ real: true; name: string }, void, { token: string; reason: string; confirm: true }>()('POST', `${A}/pulse-v3/reveal`, { honest: true });
/** POST /api/pulse/incidents/:id/unack: undo an acknowledgement, paging resumes. */
export const pulseIncidentUnack = route<PulseEnvelope<PulseIncident>>()('POST', '/api/pulse/incidents/:id/unack');
/** GET /api/pulse/projections: the Projected tab. */
export const pulseProjections = route<PulseProjections>()('GET', '/api/pulse/projections', { honest: true });
/** GET /api/pulse/suppressions */
export const pulseSuppressions = route<PulseEnvelope<PulseSuppression[]>>()('GET', '/api/pulse/suppressions');
/** POST /api/pulse/suppressions/:id/lift */
export const pulseSuppressionLift = route<PulseEnvelope<{ id: string }>>()('POST', '/api/pulse/suppressions/:id/lift');
/** GET /api/pulse/alert-rules/audit */
export const pulseRuleAudit = route<PulseEnvelope<RuleAuditFinding[]>>()('GET', '/api/pulse/alert-rules/audit');
/** GET /api/pulse/activation */
export const pulseActivation = route<PulseEnvelope<ActivationFlag[]>>()('GET', '/api/pulse/activation');
/** POST /api/pulse/oncall/override: hand the pager to someone until the next hand-off. */
export const pulseOncallOverride = route<PulseEnvelope<OnCallConfigResponse>, void, { userId: string | null }>()('POST', '/api/pulse/oncall/override');
